import { useEffect, useState } from "react";
import { FaTimes, FaChartLine } from "react-icons/fa";
import SearchIcon from '@mui/icons-material/Search';
import { BACKEND } from "../config/configEnvs";
import axios from "axios";
import LoadingScreen from "../components/ui/temp-screens/LoadingScreen";
import NoDataScreen from "../components/ui/temp-screens/NoDataScreen";
import Button from '@mui/material/Button';
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { months, years } from "../utils/rawData/dateValues";
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Checkbox } from "@mui/material";
import { CheckCircle, ClearAll, Refresh } from "@mui/icons-material";
import DinamicButton from "../components/ui/components/DinamicButton";
import SearchBar from "../components/ui/components/SearchBar";
import Selector from "../components/ui/components/Selector";

const ProyeccionIngresos = () => {

  const user = useSelector((state) => state.auth?.user)

  const [page, setPage] = useState(1);
  const [brandName, setBrandName] = useState("");

  const [year, setYear] = useState(new Date().getFullYear());

  const [loading, setLoading] = useState(false);

  const [record, setRecord] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [services, setServices] = useState([]);
  const [mode, setMode] = useState("table");
  // Obtener el mes actual (1-12)
  const currentMonth = new Date().getMonth() + 1; // getMonth() devuelve 0-11, sumamos 1 para obtener 1-12
  
  // Calcular meses seleccionados por defecto: 1 mes atrás, mes actual, 2 meses adelante
  const getDefaultSelectedMonths = () => {
    const months = [];
    
    // Mes anterior (con manejo de año anterior)
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    months.push(prevMonth);
    
    // Mes actual
    months.push(currentMonth);
    
    // 2 meses adelante (con manejo de año siguiente)
    const nextMonth1 = currentMonth === 12 ? 1 : currentMonth + 1;
    const nextMonth2 = nextMonth1 === 12 ? 1 : nextMonth1 + 1;
    months.push(nextMonth1, nextMonth2);
    
    return months.sort((a, b) => a - b); // Ordenar de menor a mayor
  };

  const [selectedMonths, setSelectedMonths] = useState(getDefaultSelectedMonths()); // Meses seleccionados por defecto: 1 atrás, actual, 2 adelante

  // Obtener datos de ingresos solo para los meses seleccionados
  const getRecord = async () => {
    try {
      setLoading(true);
      
      // Obtener datos solo para los meses seleccionados
      const allData = [];
      
      for (let month of selectedMonths) {
        let url = `${BACKEND}/api/getMonthlyIncomes?userId=${user.id}&month=${month}&year=${year}`;

        if (brandName) {
          url += `&brandName=${brandName}`;
        }

        const res = await axios.get(url);
        allData.push({
          month,
          clients: res.data.clients || []
        });
      }

      // Procesar datos para crear la estructura horizontal
      const processedData = processDataForHorizontalView(allData);
      setRecord(processedData);

    } catch (error) {
      toast.error("Error al obtener los datos");
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const getServices = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/getServices`);
      setServices(r.data.services);
    } catch (error) {
      console.log(error);
    }
  }

  // Función para procesar datos en formato horizontal
  const processDataForHorizontalView = (allData) => {
    const clientMap = new Map();

    // Procesar cada mes
    allData.forEach(({ month, clients }) => {
      clients.forEach(client => {
        const clientId = client.id;
        
        if (!clientMap.has(clientId)) {
          clientMap.set(clientId, {
            id: clientId,
            nombreComercial: client.nombreComercial,
            encargadoProyecto: client.encargadoProyecto,
            nombreGerente: client.nombreGerente,
            pais: client.pais,
            months: {}
          });
        }

        const clientData = clientMap.get(clientId);
        
        // Calcular pagos pagados (solo los que ya se pagaron)
        const paidPayments = client.pagos
          .filter(pago => pago.pagado === true)
          .reduce((acc, pago) => {
            return acc + (pago.precio || 0);
          }, 0);

        // Calcular pagos pendientes (no pagados)
        const pendingPayments = client.pagos
          .filter(pago => pago.pagado === false)
          .reduce((acc, pago) => {
            return acc + (pago.precio || 0);
          }, 0);

        // El total ahora es la suma de pagados + pendientes
        const monthTotal = paidPayments + pendingPayments;

        // Calcular desglose por servicio usando montosServicios
        const services = {};
        client.pagos.forEach(pago => {
          if (pago.montosServicios && Array.isArray(pago.montosServicios)) {
            pago.montosServicios.forEach(servicio => {
              const serviceId = servicio?.servicioId?.servicio;
              if (serviceId) {
                if (!services[serviceId]) {
                  services[serviceId] = 0;
                }
                services[serviceId] += servicio.monto || 0;
              }
            });
          }
        });

        clientData.months[month] = {
          total: monthTotal,
          paid: paidPayments,
          pending: pendingPayments,
          services: services
        };
      });
    });

    return Array.from(clientMap.values());
  }

  // Calcular totales por mes
  const calculateMonthlyTotals = () => {
    const monthlyPaid = {};
    const monthlyPending = {};
    
    record.forEach(client => {
      Object.keys(client.months).forEach(month => {
        if (!monthlyPaid[month]) {
          monthlyPaid[month] = 0;
        }
        if (!monthlyPending[month]) {
          monthlyPending[month] = 0;
        }
        monthlyPaid[month] += client.months[month].paid;
        monthlyPending[month] += client.months[month].pending;
      });
    });

    // Calcular totales generales
    const grandPaidTotal = Object.values(monthlyPaid).reduce((sum, paid) => sum + paid, 0);
    const grandPendingTotal = Object.values(monthlyPending).reduce((sum, pending) => sum + pending, 0);
    const grandTotal = grandPaidTotal + grandPendingTotal;

    return { monthlyPaid, monthlyPending, grandTotal, grandPaidTotal, grandPendingTotal };
  }

  const { monthlyPaid, monthlyPending, grandTotal, grandPaidTotal, grandPendingTotal } = calculateMonthlyTotals();
  
  const handleResetSearch = () => {
    setBrandName("");
  }
  
  const handleSearchClients = () => {
    setPage(1)
    getRecord()
  };

  const handleMonthToggle = (month) => {
    setSelectedMonths(prev => {
      if (prev.includes(month)) {
        return prev.filter(m => m !== month);
      } else {
        return [...prev, month].sort((a, b) => a - b);
      }
    });
  };

  const handleSelectAllMonths = () => {
    setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  };

  const handleClearAllMonths = () => {
    setSelectedMonths([]);
  };

  useEffect(() => { 
    if (brandName === "") {
      getRecord(); 
      setPage(1); 
    }
  }, [brandName]);

  useEffect(() => {
    getServices();
  }, []);

  useEffect(() => {
    getRecord();
  }, [year]);

  // Obtener meses que tienen datos y están seleccionados
  const getMonthsWithData = () => {
    const monthsWithData = new Set();
    record.forEach(client => {
      Object.keys(client.months).forEach(month => {
        const monthNum = parseInt(month);
        if (selectedMonths.includes(monthNum)) {
          monthsWithData.add(monthNum);
        }
      });
    });
    return Array.from(monthsWithData).sort((a, b) => a - b);
  };

  const monthsWithData = getMonthsWithData();

  // Preparar datos para el gráfico de barras (ingresos por mes)
  const chartData = monthsWithData.map(month => ({
    month: months[month - 1]?.label || `Mes ${month}`,
    ingresos: (monthlyPaid[month] + monthlyPending[month]).toFixed(2) || 0,
    pagosPendientes: monthlyPending[month].toFixed(2) || 0,
    pagosPagados: monthlyPaid[month].toFixed(2) || 0
  }));

  // Preparar datos para el gráfico de pie (distribución por servicios)
  const serviceTotals = {};
  record.forEach(client => {
    Object.values(client.months).forEach(monthData => {
      Object.entries(monthData.services).forEach(([serviceId, amount]) => {
        if (!serviceTotals[serviceId]) {
          serviceTotals[serviceId] = 0;
        }
        serviceTotals[serviceId] += amount;
      });
    });
  });

  const pieData = Object.entries(serviceTotals).map(([serviceId, amount]) => {
    const service = services.find(s => s.id === serviceId);
    return {
      id: service ? service.name : serviceId,
      label: service ? service.name : serviceId,
      value: amount
    };
  }).filter(item => item.value > 0);

  return (
    <div className="flex flex-col h-screen w-full">

      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-4 py-4">
            <FaChartLine className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Proyección de ingresos</h2>
          </div>
          <div className="flex flex-row gap-4 items-center justify-center">
            <SearchBar
              value={brandName}
              setter={setBrandName}
              pageSetter={setPage}
              getter={getRecord}
              placeholder="Buscar marca..."
              text="Buscar"
              disabled={loading}
              icon={<SearchIcon />}
            />
            <Selector
              title="Modo"
              value={mode}
              setter={setMode}
              width="w-[8rem]"
              options={[{label: "Tabla", value: "table"}, {label: "Gráfico", value: "chart"}]}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* Filtros de meses */}
      <div className="flex flex-row items-center justify-between w-full bg-gray-100 mb-0.5 mt-0.5 px-6 gap-2">
        <div className="flex flex-row items-center justify-between w-full">

          <div className="flex flex-row gap-4 items-center justify-center">
            
            {/* Botones de seleccionar todos */}
            <DinamicButton
              content="Todos"
              action={() => handleSelectAllMonths()}
              icon={<CheckCircle />}
              iconSize={16}
              width="w-[12rem]"
              backgroundColor="#2563eb"
              hoverBackgroundColor="#1d4ed8"
            />

            <DinamicButton
              content="Ninguno"
              action={() => handleClearAllMonths()}
              icon={<ClearAll />}
              iconSize={16}
              width="w-[12rem]"
              backgroundColor="#2563eb"
              hoverBackgroundColor="#1d4ed8"
            />

            {/* Filtro de meses */}
            <FormControl sx={{ borderRadius: "8px", height: 32, width: 300, backgroundColor: "white" }}>
              <InputLabel
                sx={{
                  fontSize: 12,
                  transform: "translate(14px, 8px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -6px) scale(0.75)",
                  },
                }}
              >
                Filtrar por Meses
              </InputLabel>
              <Select
                labelId="months-filter-label"
                sx={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  fontSize: 12,
                  borderRadius: "8px",
                }}
                id="months-filter"
                multiple
                value={selectedMonths}
                onChange={(event) => setSelectedMonths(event.target.value)}
                input={<OutlinedInput label="Filtrar por Meses" sx={{ height: 25, fontSize: 12 }}/>}
                renderValue={(selected) => {
                  if (selected.length === 12) return "Todos los meses";
                  if (selected.length === 0) return "Ningún mes";
                  return selected.map(month => months[month - 1]?.label || `Mes ${month}`).join(', ');
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 6.5,
                      width: 200,
                    }
                  }
                }}
              >
                {months.map((month, index) => (
                  <MenuItem key={index + 1} value={index + 1} sx={{ height: "30px" }}>
                    <Checkbox checked={selectedMonths.includes(index + 1)} />
                    <ListItemText primary={month.label} primaryTypographyProps={{ fontSize: 12, fontFamily: "poppins" }}/>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Aplicar */}
            <DinamicButton
              content="Aplicar filtros"
              action={() => handleSearchClients()}
              icon={<Refresh />}
              iconSize={16}
              width="w-[12rem]"
            />
          </div>
          <Selector
            title="Año"
            value={year}
            setter={setYear}
            width="w-[8rem]"
            options={years}
            disabled={loading}
          />
        </div>
      </div>

      {/* Body */}
      <div className="flex-grow overflow-y-auto">
        {loading ? (
          <LoadingScreen />
        ) : (
          record.length === 0 ? (
            <NoDataScreen />
          ) : (
            mode === "table" ? (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-200 z-50">
                  <tr className="text-gray-700 text-center text-xs font-bold z-50">
                    <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 sticky left-0 text-white z-50">
                      Cliente
                    </th>
                    <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                      Total
                    </th>
                    <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                      Pendientes
                    </th>
                    <th className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                      Pagado
                    </th>
                    {monthsWithData.map(month => (
                      <th key={month} className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                        {months[month - 1]?.label || `Mes ${month}`}
                      </th>
                    ))}
                  </tr>
                  {/* Fila de totales */}
                  <tr className="bg-gray-100 font-bold text-xs text-center">
                    <td className="py-2 border border-gray-300">Totales</td>
                    <td className="text-black py-2 border border-gray-300">
                      {grandTotal > 0 ? `S/. ${grandTotal.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
                    </td>
                    <td className="text-red-600 py-2 border border-gray-300">
                      {grandPendingTotal > 0 ? `S/. ${grandPendingTotal.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
                    </td>
                    <td className="text-green-500 py-2 border border-gray-300">
                      {grandPaidTotal > 0 ? `S/. ${grandPaidTotal.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
                    </td>
                    {monthsWithData.map(month => (
                      <td key={month} className="gap-2 py-2 border border-gray-300">
                        <span className="text-black mr-2">{(monthlyPaid[month] + monthlyPending[month]) > 0 ? `S/. ${(monthlyPaid[month] + monthlyPending[month]).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</span>
                        <span className="text-green-500 mr-2">{monthlyPaid[month] > 0 ? `S/. ${monthlyPaid[month].toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</span>
                        <span className="text-red-600">{monthlyPending[month] > 0 ? `S/. ${monthlyPending[month].toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</span>
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {record.map((client) => {
                    const clientTotal = Object.values(client.months).reduce((sum, monthData) => sum + monthData.total, 0);
                    const clientPendingTotal = Object.values(client.months).reduce((sum, monthData) => sum + monthData.pending, 0);
                    const clientPaidTotal = Object.values(client.months).reduce((sum, monthData) => sum + monthData.paid, 0);
                    
                    return (
                      <tr key={client.id} className="text-xs py-1 w-full hover:bg-gray-100 transition-all duration-100 z-10">
                        <td className="px-4 py-2 border-r border-r-gray-300 border-b border-b-gray-300  border-t border-t-gray-300 sticky left-0 z-10 bg-white shadow-sm"> 
                          <div className="flex flex-row gap-2 items-center justify-between w-[15rem] max-w-[15rem]">
                            <span className="">{client.nombreComercial}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center border border-gray-300">
                          {clientTotal > 0 ? `S/. ${clientTotal.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center border border-gray-300 text-red-600">
                          {clientPendingTotal > 0 ? `S/. ${clientPendingTotal.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center border border-gray-300 text-green-500">  
                          {clientPaidTotal > 0 ? `S/. ${clientPaidTotal.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
                        </td>
                        {monthsWithData.map(month => {
                          const monthData = client.months[month];
                          if (!monthData) {
                            return (
                              <td key={month} className="px-4 py-2 whitespace-nowrap text-center border border-gray-300 text-gray-200">
                                -
                              </td>
                            );
                          }
                          
                          return (
                            <td key={month} className="px-4 py-2 whitespace-nowrap text-center border border-gray-300">
                               <div className="flex flex-col">
                                 <span className="font-bold">
                                   Total: {monthData.total > 0 ? monthData.total.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                                 </span>
                                 {monthData.pending > 0 && (
                                   <span className="text-xs text-red-600">
                                     Pendiente: {monthData.pending.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                   </span>
                                 )}
                                 {monthData.paid > 0 && (
                                   <span className="text-xs text-green-500">
                                     Pagado: {monthData.paid.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                   </span>
                                 )}
                                 {Object.keys(monthData.services).length > 0 && (
                                   <div className="text-xs text-gray-600">
                                     {Object.entries(monthData.services).map(([serviceId, amount], idx) => {
                                       const service = services.find(s => s.id === serviceId);
                                       const serviceName = service ? service.name : serviceId;
                                       return (
                                         <div key={serviceId} className="text-xs">
                                           {serviceName}: {amount.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                         </div>
                                       );
                                     })}
                                   </div>
                                 )}
                               </div>
                             </td>
                           );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-4">
                <div className="flex flex-col gap-2">

                  {/* Gráfico de barras - Ingresos por mes */}
                  <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-bold mb-4 text-gray-800">Ingresos por Mes</h3>
                    <div className="h-96">
                      <ResponsiveBar
                        data={chartData}
                        keys={['pagosPagados', 'pagosPendientes']}
                        indexBy="month"
                        margin={{ top: 50, right: 10, bottom: 50, left: 80 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        colors={['#22c55e', '#F57373']}
                        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          tickSize: 3,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Mes',
                          legendPosition: 'middle',
                          legendOffset: 32
                        }}
                        axisLeft={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Ingresos (S/.)',
                          legendPosition: 'middle',
                          legendOffset: -60
                        }}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        animate={true}
                        motionStiffness={90}
                        motionDamping={15}
                          tooltip={({ data }) => (
                           <div style={{
                             background: 'white',
                             padding: '9px 12px',
                             border: '1px solid #ccc',
                             borderRadius: '4px'
                           }}>
                             <strong>{data.month}:</strong><br />
                             <span>Total: S/. {data.ingresos?.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span><br />
                             <span>Pagado: S/. {data.pagosPagados?.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span><br />
                             <span>Pendientes: S/. {data.pagosPendientes?.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                           </div>
                         )}
                      />
                    </div>
                  </div>

                  {/* Gráfico de pie - Distribución por servicios */}
                  <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-bold mb-4 text-gray-800">Distribución por Servicios</h3>
                    <div className="h-96">
                      <ResponsivePie
                        data={pieData}
                        margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={8}
                        borderWidth={1}
                        colors={{ scheme: 'paired' }}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsTextColor="#333333"
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                        defs={[
                          {
                            id: 'dots',
                            type: 'patternDots',
                            background: 'inherit',
                            color: 'rgba(255, 255, 255, 0.3)',
                            size: 4,
                            padding: 1,
                            stagger: true
                          },
                          {
                            id: 'lines',
                            type: 'patternLines',
                            background: 'inherit',
                            color: 'rgba(255, 255, 255, 0.3)',
                            rotation: -45,
                            lineWidth: 6,
                            spacing: 10
                          }
                        ]}
                        fill={[
                          {
                            match: {
                              id: 'ruby'
                            },
                            id: 'dots'
                          },
                          {
                            match: {
                              id: 'c'
                            },
                            id: 'dots'
                          },
                          {
                            match: {
                              id: 'go'
                            },
                            id: 'dots'
                          },
                          {
                            match: {
                              id: 'python'
                            },
                            id: 'dots'
                          },
                          {
                            match: {
                              id: 'scala'
                            },
                            id: 'lines'
                          },
                          {
                            match: {
                              id: 'lisp'
                            },
                            id: 'lines'
                          },
                          {
                            match: {
                              id: 'elixir'
                            },
                            id: 'lines'
                          },
                          {
                            match: {
                              id: 'javascript'
                            },
                            id: 'lines'
                          }
                        ]}
                        legends={[
                          {
                            anchor: 'right',
                            direction: 'column',
                            justify: false,
                            translateX: 0,
                            translateY: 0,
                            itemsSpacing: 0,
                            itemWidth: 100,
                            itemHeight: 18,
                            itemTextColor: '#999',
                            itemDirection: 'left-to-right',
                            itemOpacity: 1,
                            symbolSize: 18,
                            symbolShape: 'circle'
                          }
                        ]}
                        tooltip={({ datum }) => (
                          <div style={{
                            background: 'white',
                            padding: '9px 12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                          }}>
                            <strong>{datum.label}:</strong> S/. {datum.value.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                  
                </div>
              </div>
            )
          )
        )}
      </div>
      
    </div>
  )
}

export default ProyeccionIngresos 